import React from 'react';
import { ContentHeadline } from '../../../../_lib/components/ContentHeadline';
import { ContentParagraph, ContentParagraphModifier } from '../../../../_lib/components/ContentParagraph';
import { DateFormat, DateFormats } from '../../../../_lib/components/DateFormat';
import { useAnnouncement } from './useAnnouncement';
import { AnnouncementConfirmationIndicator } from '../AnnouncementConfirmationIndicator';
import { AnnouncementSenderRecipient } from '../AnnouncementSenderRecipient';
import { useSonTranslation } from '../../../../translation/useSonTranslation';

import type { AnnouncementProps } from '../../Announcement.types';
import { AnnouncementCategoryDescription } from './AnnouncementCategoryDescription';
import { AnnouncementCategory } from './AnnouncementCategory';
import { AnnouncementCategoryImage } from './AnnouncementCategoryImage';
import { FavoriteButton } from '../../../../components/forms/FavoriteButton/FavoriteButton';
import { Namespace } from '../../../../translation/namespace';
import { Icons } from '../../../../components/atoms/SVG';
import { BtnModifier } from '../../../../components/molecules/Btn';
import { useBlockContext, useRefreshBlock } from '../../../../_lib/BlockFetcher';
import { useDelayed } from '../../../../hooks/useDelayed';
import { BlockType } from '../../../../types/api/ElementType';
import { ContentHeadlineModifier } from '../../../../_lib/components/ContentHeadline/ContentHeadline';

export function Announcement(props: AnnouncementProps) {
  const { t } = useSonTranslation('Announcement');
  const { elementType } = useBlockContext();
  const { refresh } = useRefreshBlock();
  const delayedRefresh = useDelayed(refresh, 100);

  const {
    confirmationInfoText,
    renderInteractionButtons,
    renderRecipients,
    getHeadlineHierarchy,
    renderSender,
    announcementInteractions,
  } = useAnnouncement(props);

  const {
    mandatory,
    confirmationInfo,
    id,
    title,
    titleUrl,
    text,
    startDate,
    allDay,
    recipients,
    sender,
    category,
    bookmark,
    index,
  } = props;

  const getAnnouncementClass = () => {
    if (mandatory) {
      if (props.confirmed) {
        return 'announcement announcement--mandatory announcement--mandatory-confirmed';
      }
      if (confirmationInfoText) {
        return 'announcement announcement--mandatory-with-meter';
      }
      return 'announcement announcement--mandatory';
    }
    return 'announcement';
  };
  return (
    <div className={getAnnouncementClass()} id={id}>
      {mandatory && confirmationInfo && (
        <AnnouncementConfirmationIndicator
          id={id}
          confirmationInfo={confirmationInfo}
          confirmationInfoText={confirmationInfoText}
        />
      )}
      {mandatory && !confirmationInfo && (
        <span className="announcement__type font-regular-13-13">{t('mandatory.text')}</span>
      )}
      <div className="announcement__interactions-container-top">{renderInteractionButtons()}</div>
      <article className="announcement__content">
        {category ? (
          <AnnouncementCategory>
            <AnnouncementCategoryImage image={category.image} />
            <div>
              <AnnouncementCategoryDescription>{category.title}</AnnouncementCategoryDescription>
              <ContentHeadline
                title={title}
                url={titleUrl}
                modifier={ContentHeadlineModifier.NO_MARGIN_BOTTOM}
                hierarchy={getHeadlineHierarchy()}
              />
            </div>
          </AnnouncementCategory>
        ) : (
          <ContentHeadline title={title} url={titleUrl} hierarchy={getHeadlineHierarchy()} />
        )}
        {text && text !== '' && (
          <ContentParagraph modifier={ContentParagraphModifier.MARGIN_BOTTOM}>{text}</ContentParagraph>
        )}
        {
          // startDate can be empty in readProtected Messages
          startDate && (
            <DateFormat
              date={startDate}
              className="announcement__date font-regular-15-21"
              format={allDay ? DateFormats.DATE : DateFormats.DATE_TIME}
            />
          )
        }
        {recipients || sender ? (
          <AnnouncementSenderRecipient
            renderRecipients={renderRecipients}
            renderSender={renderSender}
            recipients={recipients}
          />
        ) : null}
        {mandatory && announcementInteractions()}
        {bookmark && (
          <div className="announcement__interactions-container-bottom">
            <FavoriteButton
              id={'announcements-bookmark-form-' + id}
              favorite={bookmark}
              translationNamespace={Namespace.Announcement}
              favoriteText={t('interactions.bookmark.button.removeBookmark', { count: index })}
              noFavoriteText={t('interactions.bookmark.button.addBookmark', { count: index })}
              svgspriteId={Icons.BOOKMARK}
              color={BtnModifier.MINOR_ACTION_ACTIVATED_DARK}
              onSuccess={elementType === BlockType.allannouncementsinbox ? delayedRefresh : undefined}
            />
          </div>
        )}
      </article>
    </div>
  );
}
